import StyledInput from "notes-and-forms-v2/components/styled-input/StyledInput";
import AnswerFieldContent from "../../answer-field-content/AnswerFieldContent";
import {
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../AtomicElementView";
import styles from "./RadiosContent.module.scss";

interface RadiosData {
  selectedOption?: {
    id: string;
    title: string;
    answerFields?: { id: string; title: string; value: string }[];
  };
  otherOption?: {
    value: string;
  };
  // this structure can be extended with new fields in the future, but changing or removing existing fields should be avoided
}

function parseRawData(data: any, element: RadiosElement): RadiosData {
  if (!data) {
    return {};
  }

  if (typeof data === "object" && !Array.isArray(data)) {
    // We assume the data is in the current / latest format
    return data;
  }

  // Otherwise, the data can be:
  // - a string containing an option title (saved by the initial implementation of RadiosContent)
  // - a string containing an option id (returned by GradientJ)
  // - an array of strings (returned by GradientJ, we'll try to match the first as an option title or id, the rest will be ignored)

  const value = Array.isArray(data) ? data[0] : data.toString();
  // We'll try to match the value to an option
  const option = element.options.find(
    (option) => option.title === value || option.id === value,
  );

  if (option) {
    return {
      selectedOption: {
        id: option.id,
        title: option.title,
      },
    };
  }

  if (value && element.enableOtherOption) {
    return {
      otherOption: {
        value: value,
      },
    };
  }

  return {};
}

export function RadiosContentForClipboard({
  data: rawData,
  element,
}: ElementViewForClipboardProps<RadiosElement, RadiosData>) {
  const data = parseRawData(rawData, element);
  if (data.selectedOption) {
    return (
      <div>
        {data.selectedOption.title}
        {data.selectedOption.answerFields?.map((answerField) => (
          <div>{answerField.value}</div>
        ))}
      </div>
    );
  }

  if (data.otherOption) {
    return <div>{data.otherOption.value}</div>;
  }

  return <div>No answer</div>;
}

export default function RadiosContent({
  data: rawData,
  element,
  onChange,
  isLocked,
  isDownloadMode,
  disabled,
  options,
}: ElementViewProps<RadiosElement, RadiosData>) {
  const data = parseRawData(rawData, element);

  return (
    <div className={styles.container}>
      <div>
        {element.options.map((option) => {
          const isSelected = data.selectedOption?.id === option.id;

          if (options?.hideUnselectedOptionsForRadios && !isSelected) {
            return null;
          }

          return (
            <div className={styles["option-row"]} key={option.id}>
              <div className={styles.option}>
                <StyledInput
                  type="radio"
                  checked={isSelected}
                  onChange={() =>
                    onChange({
                      selectedOption: {
                        id: option.id,
                        title: option.title,
                      },
                    })
                  }
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
                <label
                  onClick={() => {
                    if (disabled || isLocked) {
                      return;
                    }
                    onChange({
                      selectedOption: {
                        id: option.id,
                        title: option.title,
                      },
                    });
                  }}
                >
                  <div className={styles["option__title"]}>{option.title}</div>
                </label>
              </div>
              {data.selectedOption?.id === option.id &&
                option.answerFields?.map((answerField) => (
                  <AnswerFieldContent
                    key={answerField.id}
                    answerField={answerField}
                    data={data.selectedOption?.answerFields?.find(
                      (el) => el.id === answerField.id,
                    )}
                    onChange={(answerFieldData) =>
                      onChange({
                        selectedOption: {
                          id: option.id,
                          title: option.title,
                          answerFields: [answerFieldData], // No support for multiple answer fields for now
                        },
                      })
                    }
                    disabled={disabled || isLocked}
                  />
                ))}
            </div>
          );
        })}

        {element.enableOtherOption &&
          (!options?.hideUnselectedOptionsForRadios || data.otherOption) && (
            <div className={styles["option-row"]}>
              <div className={styles.option}>
                <StyledInput
                  type="radio"
                  checked={!!data.otherOption}
                  onChange={() => {
                    onChange({
                      otherOption: {
                        value: "",
                      },
                    });
                  }}
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
                <label>
                  <div className={styles["option__title"]}>
                    Other:
                    <input
                      className={styles["option__title-input"]}
                      placeholder="Please specify"
                      value={data.otherOption?.value ?? ""}
                      onChange={(e) => {
                        onChange({
                          otherOption: {
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </label>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
